import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Tag, Button, Typography, Spin, Alert, Descriptions,
  Table, Popconfirm, Select, Modal, message, Divider, Space, Badge,
} from 'antd';
import {
  ArrowLeftOutlined, StopOutlined, CheckCircleOutlined, CrownOutlined,
  DeleteOutlined, ReloadOutlined, MailOutlined, UserOutlined,
  CheckOutlined, CloseOutlined, DownloadOutlined, ThunderboltOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { InputNumber } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { theme as antdTheme } from 'antd';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PLAN_COLOR  = { free: 'default', pro: 'gold' };
const STATUS_COLOR = { draft: 'default', scheduled: 'processing', sending: 'warning', completed: 'success', paused_high_bounce: 'error', paused_duplicates: 'error' };

const AdminOrgDetail = () => {
  const { id }            = useParams();
  const navigate          = useNavigate();
  const { token: tk }     = antdTheme.useToken();
  const [data, setData]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [planModal, setPlanModal]   = useState(false);
  const [newPlan, setNewPlan]       = useState('pro');
  const [actionLoading, setAction]  = useState('');
  const [exportingCSV, setExporting] = useState(false);
  const [sendRate,     setSendRate]  = useState(1);
  const [rateLoading,  setRateLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/admin/orgs/${id}`, { headers: authHeader() });
      setData(res.data);
      setNewPlan(res.data.org.plan === 'pro' ? 'free' : 'pro');
      setSendRate(res.data.org.ses_send_rate || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load organisation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const doAction = async (action, body = {}) => {
    setAction(action);
    try {
      if (action === 'delete') {
        await axios.delete(`/api/admin/orgs/${id}`, { headers: authHeader() });
        message.success('Organisation deleted');
        navigate('/admin/orgs');
        return;
      }
      if (action === 'plan') {
        await axios.put(`/api/admin/orgs/${id}/plan`, { plan: newPlan }, { headers: authHeader() });
        message.success(`Plan set to ${newPlan}`);
        setPlanModal(false);
      } else {
        await axios.put(`/api/admin/orgs/${id}/${action}`, body, { headers: authHeader() });
        message.success(`Action "${action}" applied`);
      }
      await load();
    } catch (err) {
      message.error(err.response?.data?.message || 'Action failed');
    } finally {
      setAction('');
    }
  };

  const handleSendRateUpdate = async () => {
    if (!sendRate || sendRate < 1) return;
    setRateLoading(true);
    try {
      await axios.put(`/api/admin/orgs/${id}/send-rate`, { ses_send_rate: sendRate }, { headers: authHeader() });
      message.success(`Send rate updated to ${sendRate}/s`);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update send rate');
    } finally {
      setRateLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`/api/admin/orgs/${id}/subscribers/export`, {
        headers:      authHeader(),
        responseType: 'blob',
      });
      const url      = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const filename = res.headers['content-disposition']?.match(/filename="([^"]+)"/)?.[1]
                       || `subscribers_org${id}.csv`;
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      message.success('CSV downloaded');
    } catch {
      message.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Spin size="large" />
    </div>
  );
  if (error) return <Alert type="error" message={error} showIcon style={{ borderRadius: 10 }} />;

  const { org, users, recent_campaigns, total_emails_sent } = data;

  const userCols = [
    { title: 'Name',  dataIndex: 'name',  key: 'name',  render: v => v || '—' },
    { title: 'Email', dataIndex: 'email', key: 'email',
      render: v => <Text copyable style={{ fontSize: 13 }}>{v}</Text> },
    { title: 'Role',  dataIndex: 'role',  key: 'role',
      render: v => <Tag>{v}</Tag> },
    { title: 'Email Verified', dataIndex: 'email_verified', key: 'ev',
      render: v => v ? <CheckOutlined style={{ color: '#10b981' }} /> : <CloseOutlined style={{ color: '#ef4444' }} /> },
    { title: 'Joined', dataIndex: 'created_at', key: 'created_at',
      render: v => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
  ];

  const campaignCols = [
    { title: 'Campaign', dataIndex: 'name', key: 'name',
      render: (name, row) => <div><div style={{ fontWeight: 500 }}>{name}</div><div style={{ fontSize: 12, color: tk.colorTextSecondary }}>{row.subject}</div></div> },
    { title: 'Status', dataIndex: 'status', key: 'status',
      render: s => <Tag color={STATUS_COLOR[s] || 'default'}>{s?.replace('_', ' ')}</Tag> },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at',
      render: v => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) },
  ];

  return (
    <div>
      {/* Back + header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="text" icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/orgs')}
          style={{ marginBottom: 8, paddingLeft: 0 }}
        >
          All Organisations
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>{org.name}</Title>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <Tag icon={org.plan === 'pro' ? <CrownOutlined /> : null} color={PLAN_COLOR[org.plan]}>
                {org.plan?.toUpperCase()}
              </Tag>
              {org.suspended && <Tag color="error" icon={<StopOutlined />}>Suspended</Tag>}
              {org.ses_verified
                ? <Badge status="success" text="SES Connected" />
                : <Badge status="warning" text="SES Not Connected" />}
            </div>
          </div>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
            <Button
              icon={<DownloadOutlined />}
              loading={exportingCSV}
              onClick={handleExportCSV}
            >
              Export Subscribers CSV
            </Button>
            {org.suspended ? (
              <Button
                type="primary" icon={<CheckCircleOutlined />}
                loading={actionLoading === 'unsuspend'}
                onClick={() => doAction('unsuspend')}
              >
                Unsuspend
              </Button>
            ) : (
              <Popconfirm
                title={`Suspend "${org.name}"?`}
                description="Their sends will be blocked immediately."
                onConfirm={() => doAction('suspend')}
                okText="Suspend" okButtonProps={{ danger: true }}
              >
                <Button danger icon={<StopOutlined />} loading={actionLoading === 'suspend'}>
                  Suspend
                </Button>
              </Popconfirm>
            )}
            <Popconfirm
              title="Reset campaign counter?"
              description="Sets campaigns_this_month back to 0 for this org."
              onConfirm={() => doAction('reset-counter')}
              okText="Reset"
            >
              <Button icon={<RedoOutlined />} loading={actionLoading === 'reset-counter'}>
                Reset Counter
              </Button>
            </Popconfirm>
            <Button icon={<CrownOutlined />} onClick={() => setPlanModal(true)}>Override Plan</Button>
            <Popconfirm
              title={`Delete "${org.name}"?`}
              description="Permanently deletes ALL campaigns, subscribers and data. This cannot be undone."
              onConfirm={() => doAction('delete')}
              okText="Delete Forever" okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} loading={actionLoading === 'delete'}>
                Delete Org
              </Button>
            </Popconfirm>
          </Space>
        </div>
      </div>

      <Row gutter={[20, 20]}>
        {/* Org details */}
        <Col xs={24} lg={14}>
          <Card
            title="Organisation Details"
            style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 20 }}
          >
            <Descriptions column={2} size="small" labelStyle={{ color: tk.colorTextSecondary }}>
              <Descriptions.Item label="ID">{org.id}</Descriptions.Item>
              <Descriptions.Item label="Plan Status">{org.plan_status}</Descriptions.Item>
              <Descriptions.Item label="Campaigns/mo">{org.campaigns_this_month}</Descriptions.Item>
              <Descriptions.Item label="Total Emails Sent">{total_emails_sent?.toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="SES From">{org.ses_from_email || '—'}</Descriptions.Item>
              <Descriptions.Item label="SES Region">{org.ses_region || '—'}</Descriptions.Item>
              <Descriptions.Item label="Send Rate">{org.ses_send_rate || 1}/s</Descriptions.Item>
              {org.stripe_customer_id && (
                <Descriptions.Item label="Stripe Customer" span={2}>
                  <Text copyable style={{ fontSize: 12 }}>{org.stripe_customer_id}</Text>
                </Descriptions.Item>
              )}
              {org.plan_expires_at && (
                <Descriptions.Item label="Plan Renews">
                  {new Date(org.plan_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Onboarding">
                {org.onboarding_complete
                  ? <CheckOutlined style={{ color: '#10b981' }} />
                  : <CloseOutlined style={{ color: '#ef4444' }} />}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(org.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Users */}
          <Card
            title={<span><UserOutlined style={{ marginRight: 6 }} />Users ({users.length})</span>}
            style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
          >
            <Table
              dataSource={users}
              columns={userCols}
              rowKey="id"
              size="small"
              pagination={false}
            
          scroll={{ x: 'max-content' }}
        />
          </Card>
        </Col>

        {/* Right column: send rate + recent campaigns */}
        <Col xs={24} lg={10}>
          {/* Send Rate Control */}
          <Card
            title={<span><ThunderboltOutlined style={{ marginRight: 6, color: '#f59e0b' }} />Max Send Rate</span>}
            style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 20 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Text style={{ fontSize: 13, color: tk.colorTextSecondary }}>
                Controls how many emails per second the BullMQ worker sends for this org.
                SES sandbox limit is <strong>1/s</strong>; production accounts vary.
                Restart the worker after changing this value for it to take effect.
              </Text>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <InputNumber
                  min={1}
                  max={1000}
                  step={1}
                  value={sendRate}
                  onChange={val => setSendRate(val)}
                  addonAfter="emails / sec"
                  style={{ width: 200 }}
                />
                <Button
                  type="primary"
                  loading={rateLoading}
                  onClick={handleSendRateUpdate}
                  disabled={!sendRate || sendRate < 1}
                  style={{ borderRadius: 8 }}
                >
                  Save
                </Button>
              </div>
              <Text style={{ fontSize: 12, color: '#9ca3af' }}>
                Default: <strong>1/s</strong> (sandbox) · Ask org owner for their SES production limit
              </Text>
            </div>
          </Card>

          {/* Recent campaigns */}
          <Card
            title={<span><MailOutlined style={{ marginRight: 6 }} />Recent Campaigns</span>}
            style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
          >
            <Table
              dataSource={recent_campaigns}
              columns={campaignCols}
              rowKey="id"
              size="small"
              pagination={false}
              locale={{ emptyText: 'No campaigns yet' }}
            
          scroll={{ x: 'max-content' }}
        />
          </Card>
        </Col>
      </Row>

      {/* Plan override modal */}
      <Modal
        open={planModal}
        title="Override Plan"
        onCancel={() => setPlanModal(false)}
        onOk={() => doAction('plan')}
        confirmLoading={actionLoading === 'plan'}
        okText="Apply"
      >
        <div style={{ padding: '12px 0' }}>
          <Text style={{ display: 'block', marginBottom: 12 }}>
            Current plan: <strong>{org.plan?.toUpperCase()}</strong>
          </Text>
          <Select value={newPlan} onChange={setNewPlan} style={{ width: '100%' }}>
            <Option value="free">Free</Option>
            <Option value="pro">Pro (manual grant — no Stripe required)</Option>
          </Select>
          {newPlan === 'pro' && (
            <Text style={{ display: 'block', marginTop: 8, color: '#f59e0b', fontSize: 13 }}>
              This grants Pro access for 1 year without a Stripe subscription.
            </Text>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AdminOrgDetail;
